/**
 * Link Manager modal — site-wide link inventory with individual edit and bulk find-and-replace.
 * Uses DOM-based parsing (DOMParser) and composite anchor signatures for stable targeting.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { showConfirmModal, showToast, showUndoToast } from './shell.js';
import { bridge } from '../editing/bridge.js';
import { createModal, setButtonBusy, isButtonBusy } from './components.js';
import {
  findAnchorBySignature,
  scanAllLinks as scanAllLinksInState
} from '../shared/section-link-scan.js';

export { findAnchorBySignature };
export { getDomPath, scanSectionLinks } from '../shared/section-link-scan.js';

/**
 * Scan entire state for all links. Deduplicates shared sections (each shared
 * section is visited exactly once via iterateAllSections; one mutation
 * propagates to every page that uses it).
 */
export function scanAllLinks() {
  return scanAllLinksInState(store.getState());
}

/**
 * Update a single link's href in state.
 */
function updateLinkHref(entry, newHref) {
  const state = store.getState();
  const html = getByPath(state, entry.statePath);
  if (!html) return false;

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  const match = findAnchorBySignature(root, entry.anchorSignature);
  if (!match) return false;

  match.anchor.setAttribute('href', newHref);
  store.update(entry.statePath, root.innerHTML, 'Update link');
  return true;
}

/**
 * Bulk replace all hrefs matching `fromHref` with `toHref`.
 * Deduplicates by statePath so shared sections are only patched once.
 */
function bulkReplaceHrefs(links, fromHref, toHref) {
  const state = store.getState();
  const patchedPaths = new Map(); // statePath → new HTML

  for (const entry of links) {
    if (entry.anchorSignature.href !== fromHref) continue;

    // If we haven't parsed this section yet, parse it
    if (!patchedPaths.has(entry.statePath)) {
      const html = getByPath(state, entry.statePath);
      if (!html) continue;
      const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
      patchedPaths.set(entry.statePath, doc.body.firstElementChild);
    }

    const root = patchedPaths.get(entry.statePath);
    const match = findAnchorBySignature(root, entry.anchorSignature);
    if (match) {
      match.anchor.setAttribute('href', toHref);
    }
  }

  // Apply all patches as a batch for single undo
  if (patchedPaths.size > 0) {
    store.beginTransaction('Bulk replace links');
    for (const [path, root] of patchedPaths) {
      store.update(path, root.innerHTML, 'Bulk replace link');
    }
    store.commitTransaction();
  }

  return patchedPaths.size;
}

function getByPath(obj, path) {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current == null) return undefined;
    current = current[key];
  }
  return current;
}

function escAttr(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escHTML(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function showLinkManager() {
  if (document.querySelector('.pb-link-manager-overlay')) return;

  let allLinks = scanAllLinks();

  const { body } = createModal({
    title: 'Link Manager',
    size: 'wide',
    className: 'pb-modal-scrollable',
    overlayClassName: 'pb-link-manager-overlay',
    withFooter: false,
    // PP-2: open-time focus goes through the kit, lazily — the search field is
    // built below, after this call returns.
    initialFocus: () => searchInput
  });
  body.classList.add('pb-modal-body-scroll');

  function renderTable(filter = '') {
    const filtered = filter
      ? allLinks.filter(
          (l) =>
            l.anchorSignature.href.toLowerCase().includes(filter) ||
            l.anchorSignature.textContent.toLowerCase().includes(filter) ||
            l.pageName.toLowerCase().includes(filter)
        )
      : allLinks;

    if (filtered.length === 0) {
      return '<p class="pb-props-hint" style="padding:12px">No links found.</p>';
    }

    // Group by page name
    const groups = new Map();
    for (const link of filtered) {
      const key = link.pageName;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(link);
    }
    const sortedKeys = [...groups.keys()].sort((a, b) => {
      if (a === 'All Pages') return -1;
      if (b === 'All Pages') return 1;
      return a.localeCompare(b);
    });

    let rows = '';
    for (const pageName of sortedKeys) {
      rows += `<tr class="pb-lm-group-header"><td colspan="2">${escHTML(pageName)} <span class="pb-lm-group-count">(${groups.get(pageName).length})</span></td></tr>`;
      for (const link of groups.get(pageName)) {
        const idx = allLinks.indexOf(link);
        const sig = link.anchorSignature;
        rows += `<tr data-link-idx="${idx}">
          <td class="pb-lm-cell">${escHTML(sig.textContent || '(no text)')}</td>
          <td class="pb-lm-cell pb-lm-href-cell">
            <span class="pb-lm-href" data-idx="${idx}" role="button" tabindex="0" aria-label="Edit URL ${escAttr(sig.href || '(empty)')}" data-tooltip="Click, Enter, or F2 to edit">${escHTML(sig.href || '(empty)')}</span>
          </td>
        </tr>`;
      }
    }

    return `<table class="pb-lm-table">
      <thead><tr><th>Link Text</th><th>URL</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  }

  body.innerHTML = `
        <input type="text" class="pb-input pb-lm-search" id="pb-lm-search" placeholder="Filter links…" aria-label="Filter links" spellcheck="false" />
        <div class="pb-lm-table-wrap" id="pb-lm-table-wrap">${renderTable()}</div>
        <div class="pb-lm-bulk">
          <div class="pb-lm-bulk-title">Find & Replace</div>
          <div class="pb-lm-bulk-row">
            <input type="text" class="pb-input pb-lm-bulk-input" id="pb-lm-from" placeholder="Find URL" aria-label="Find URL" spellcheck="false" />
            <input type="text" class="pb-input pb-lm-bulk-input" id="pb-lm-to" placeholder="Replace with" aria-label="Replace with URL" spellcheck="false" />
            <button class="pb-btn pb-btn-sm pb-btn-outline" id="pb-lm-replace-all">Replace All</button>
          </div>
        </div>
  `;

  const tableWrap = body.querySelector('#pb-lm-table-wrap');
  const searchInput = body.querySelector('#pb-lm-search');

  // Filter
  searchInput.addEventListener('input', () => {
    tableWrap.innerHTML = renderTable(searchInput.value.toLowerCase().trim());
  });

  const startHrefEdit = (hrefSpan) => {
    if (!hrefSpan) return;
    const idx = parseInt(hrefSpan.dataset.idx, 10);
    const link = allLinks[idx];
    if (!link) return;

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pb-input pb-lm-href-edit';
    input.value = link.anchorSignature.href;

    const cell = hrefSpan.parentElement;
    cell.innerHTML = '';
    cell.appendChild(input);
    input.focus();
    input.select();

    let committed = false;
    const commit = ({ restoreFocus = false } = {}) => {
      if (committed) return;
      committed = true;
      const newHref = input.value.trim();
      if (newHref !== link.anchorSignature.href) {
        updateLinkHref(link, newHref);
        // Re-scan to get fresh state
        allLinks = scanAllLinks();
      }
      tableWrap.innerHTML = renderTable(searchInput.value.toLowerCase().trim());
      if (restoreFocus) {
        requestAnimationFrame(() => {
          const replacement = tableWrap.querySelector(`.pb-lm-href[data-idx="${idx}"]`);
          if (replacement && typeof replacement.focus === 'function') replacement.focus();
        });
      }
    };

    input.addEventListener('blur', () => commit());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        input.blur();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        input.value = link.anchorSignature.href;
        commit({ restoreFocus: true });
      }
    });
  };

  // Click/keyboard-to-edit href
  tableWrap.addEventListener('click', (e) => {
    const hrefSpan = e.target.closest('.pb-lm-href');
    startHrefEdit(hrefSpan);
  });
  tableWrap.addEventListener('keydown', (e) => {
    const hrefSpan = e.target.closest('.pb-lm-href');
    if (!hrefSpan) return;
    if (e.key !== 'Enter' && e.key !== 'F2') return;
    e.preventDefault();
    startHrefEdit(hrefSpan);
  });

  // Bulk replace
  const replaceAllBtn = body.querySelector('#pb-lm-replace-all');
  replaceAllBtn.addEventListener('click', async () => {
    // Single-flight: without it a double click stacked two confirm dialogs for
    // the same replacement, and confirming both applied the edit twice.
    if (isButtonBusy(replaceAllBtn)) return;
    const from = body.querySelector('#pb-lm-from').value.trim();
    const to = body.querySelector('#pb-lm-to').value.trim();
    if (!from) return;
    const candidates = allLinks.filter((link) =>
      (link.anchorSignature.href || '').includes(from)
    ).length;
    if (candidates === 0) {
      showToast('No links matched that URL fragment');
      return;
    }

    setButtonBusy(replaceAllBtn, true, { busyLabel: null });
    try {
      let count = 0;
      const replaced = await showConfirmModal({
        title: 'Replace Links',
        message: `Replace "${from}" with "${to}" across all pages?`,
        details: `${candidates} link${candidates === 1 ? '' : 's'} will be updated.`,
        confirmLabel: 'Replace All',
        cancelLabel: 'Cancel',
        danger: true,
        errorScope: 'Link manager bulk replace',
        onConfirm: () => {
          count = bulkReplaceHrefs(allLinks, from, to);
        }
      });
      if (!replaced) return;

      allLinks = scanAllLinks();
      tableWrap.innerHTML = renderTable(searchInput.value.toLowerCase().trim());

      if (count > 0) {
        bridge.send('bulk-replace-hrefs', { fromHref: from, toHref: to });
        showUndoToast(`Updated ${count} link${count === 1 ? '' : 's'}`);
      } else {
        // The candidate count comes from a substring match, the replace from an
        // exact-href match, so a confirmed replace can legitimately patch
        // nothing. Reporting only the success case meant the user confirmed a
        // destructive-styled dialog and got total silence back.
        showToast('No links were changed — the URL had to match exactly.', 'info');
      }
    } finally {
      setButtonBusy(replaceAllBtn, false);
    }
  });
}
